import type { ReactNode } from "react";
import { Check } from "lucide-react";

import { Card, ButtonLink, LiveDot } from "./ui";
import { SmartLink } from "./SmartLink";
import { Reveal } from "./Reveal";
import { StoreBadges } from "./StoreBadges";
import type { Product } from "../data/products";
import { STATUS_LABEL } from "../data/products";

/**
 * One product, presented as a portfolio entry rather than a service card.
 *
 * `reverse` alternates which side the visual sits on, so a page with
 * several products reads as a sequence instead of a stack of identical
 * rows. The status badge only pulses for products genuinely in production
 * — `STATUS_LABEL` and the live dot are driven from `product.status`, so a
 * product that is still in the lab cannot accidentally be presented as
 * running.
 */
export function ProductShowcase({
  product,
  visual,
  reverse,
  secondaryHref,
  secondaryLabel,
}: {
  product: Product;
  visual: ReactNode;
  reverse?: boolean;
  secondaryHref?: string;
  secondaryLabel?: string;
}) {
  const live = product.status === "live";

  return (
    <Card className="overflow-visible p-5 sm:p-8 lg:p-12">
      <div
        className={`grid items-center gap-10 lg:gap-14 ${
          reverse
            ? "lg:grid-cols-[0.95fr_1.05fr]"
            : "lg:grid-cols-[1.05fr_0.95fr]"
        }`}
      >
        <div className={reverse ? "lg:order-2" : ""}>
          {/* A product that is not live still says where it stands. The badge
              alone read as "In the lab" and dropped `presence` on the floor —
              which is the half that tells a reader whether they can buy it. */}
          {live ? (
            <LiveDot label={product.presence} />
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/[0.05] px-3 py-1 text-xs font-medium text-white/75">
                <span className="size-1.5 rounded-full border border-white/40" />
                {STATUS_LABEL[product.status]}
              </span>
              <span className="text-xs text-white/45">{product.presence}</span>
            </span>
          )}

          <div className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40">
            {product.sector}
          </div>
          <h3 className="mt-1.5 font-display text-[2rem] font-bold leading-tight tracking-tight sm:text-[2.5rem]">
            {product.name}
          </h3>
          <p className="mt-2 text-sm text-emerald-200/90 sm:text-base">
            {product.tagline}
          </p>

          <p className="mt-5 max-w-xl text-[15px] leading-relaxed text-white/70 sm:text-lg">
            {product.summary}
          </p>

          <ul className="mt-6 grid gap-2.5 sm:grid-cols-2">
            {product.capabilities.map((c) => (
              <li key={c} className="flex gap-2.5 text-sm leading-relaxed text-white/65">
                <Check className="mt-0.5 size-4 shrink-0 text-emerald-400" />
                <span>{c}</span>
              </li>
            ))}
          </ul>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <ButtonLink href={product.href}>Explore {product.name}</ButtonLink>
            {secondaryHref ? (
              <ButtonLink href={secondaryHref} variant="ghost" icon={false}>
                {secondaryLabel ?? "Book a demo"}
              </ButtonLink>
            ) : null}
            {product.appHref ? (
              <SmartLink
                href={product.appHref}
                className="inline-flex min-h-[48px] items-center justify-center px-2 text-sm font-medium text-white/55 underline decoration-white/25 underline-offset-4 transition-colors hover:text-white/90"
              >
                Sign in
              </SmartLink>
            ) : null}
          </div>

          <StoreBadges product={product} className="mt-8" />
        </div>

        <Reveal delay={0.1} className={reverse ? "lg:order-1" : ""}>
          {visual}
        </Reveal>
      </div>
    </Card>
  );
}
